'use client'

import { useState, FormEvent, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { login } from '@/app/actions/auth'
import Image from 'next/image'
import GoogleAuthButton from '@/components/ui/google-auth-button'

export default function LoginPage() {
  const router = useRouter()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      const err = params.get('error')
      if (err) {
        if (err === 'GoogleAuthFailed') setError('La autenticación con Google falló.')
        else if (err === 'GoogleUserError') setError('No se pudo obtener la información de Google.')
        else setError('Error al autenticar con Google. Intenta nuevamente.')
      }
    }
  }, [])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await login({ email, password })
      console.log('probar', result);

      if (result.success && result.data) {
        if (result.data.role?.name === 'ADMINSISTEMA') {
          console.log('role', result.data.role?.name);

          router.push('/admin')
        } else {
          console.log('role2', result.data.role?.name);
          router.push('/dashboard')
        }
        router.refresh()
      } else {
        setError(result.error || 'Error al iniciar sesión')
      }
    } catch (_err) {
      console.error('Login error:', _err)
      setError('Error al iniciar sesión. Por favor, intenta nuevamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 lg:p-0 overflow-hidden bg-white">
      {/* Background Image full screen */}
      <div 
        className="absolute inset-0 z-0" 
        style={{ 
          backgroundImage: "url('/login-register/bg-login-register.webp')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-white/30 backdrop-blur-[2px]"></div>
      </div>

      <div className="max-w-6xl w-full px-4 md:px-8 py-8 relative z-10 flex flex-col md:flex-row items-center justify-between gap-10 md:gap-16 lg:gap-24">
        
        {/* Left Side: Floating Branding */}
        <div className="w-full md:w-1/2 flex flex-col items-center md:items-start text-center md:text-left drop-shadow-md">
          <div className="mb-4">
            <Link href="/" className="inline-block hover:opacity-90 transition-opacity">
              <span className="text-6xl md:text-[5rem] lg:text-[7rem] font-black tracking-tighter text-[#1e2c6e] flex items-center gap-2 md:gap-4 leading-none">
                <div className="flex flex-col gap-1.5 md:gap-2.5 items-center justify-center">
                  <div className="w-9 h-2 md:w-15 md:h-3.5 bg-[#1e2c6e] rounded-sm" />
                  <div className="w-12 h-2 md:w-20 md:h-3.5 bg-[#3341ab] rounded-sm transform -translate-x-1" />
                  <div className="w-9 h-2 md:w-15 md:h-3.5 bg-[#1e2c6e] rounded-sm transform translate-x-1" />
                </div>
                KERN
              </span>
            </Link>
          </div>
          <h2 className="text-xl md:text-2xl lg:text-3xl text-[#1e2c6e] font-medium leading-tight max-w-md drop-shadow-sm mix-blend-multiply opacity-90">
            Todo lo que necesita tu negocio, en un solo lugar.
          </h2>
        </div>

        {/* Right Side: Isolated Form Card */}
        <div className="w-full max-w-110 bg-white/95 backdrop-blur-md p-8 md:p-10 rounded-2xl shadow-xl border border-white/60">
          
          {error && (
            <div className="mb-5 p-3 bg-red-50/90 backdrop-blur-sm border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm font-medium text-center">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-[#1e2c6e] rounded-lg focus:ring-2 focus:ring-[#3341ab]/20 focus:border-[#3341ab] outline-none transition bg-white text-slate-800 placeholder-slate-400 font-medium text-sm shadow-sm"
                placeholder="Correo electrónico"
              />
            </div>

            <div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-[#1e2c6e] rounded-lg focus:ring-2 focus:ring-[#3341ab]/20 focus:border-[#3341ab] outline-none transition bg-white text-slate-800 placeholder-slate-400 font-medium text-sm shadow-sm"
                placeholder="Contraseña"
              />
            </div>

            <div className="pt-4 flex justify-center">
              <button
                type="submit"
                disabled={loading}
                className="w-full max-w-60 bg-[#1e2c6e] text-white h-12 rounded-full font-bold text-sm tracking-wide hover:bg-[#3341ab] transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
            </div>
          </form>

          <div className="mt-6 mb-10 text-center space-y-2">
            <p className="text-xs text-slate-700 font-medium">
              ¿Aún no tienes una cuenta?{' '}
              <Link href="/register" className="font-bold text-[#1e2c6e] hover:underline hover:text-[#3341ab] transition-colors">
                Regístrate
              </Link>
            </p>
            <Link href="/forgot-password" className="block text-xs text-[#1e2c6e] font-bold hover:underline hover:text-[#3341ab] transition-colors">
              ¿Olvidaste tu contraseña?
            </Link>
          </div>

          <div className="mt-10">
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-[10px]">
                <span className="px-3 bg-white text-slate-400 font-bold uppercase tracking-wider">o ingresar con</span>
              </div>
            </div>

            <div className="flex justify-center">
              <GoogleAuthButton />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
